import { useAspenFeatures } from "@/hooks/useAspenFeatures";
import { useEffect, useMemo, useState } from "react";
import { NavLink, useLocation } from "react-router";
import { useSettingsContext } from "sj-ui-toolkit/src/atlas/contexts/Settings/SettingsContextProvider";
import { useUserContext } from "sj-ui-toolkit/src/atlas/contexts/User/UserContextProvider";
import { C, themeClasses } from "sj-ui-toolkit/src/atlas/utils";
import { AdminAppRoutes } from "sj-ui-toolkit/src/constants/routes";
import { sanitizeTestId } from "sj-ui-toolkit/src/utils/string";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface RouteNode {
  name?: string;
  route?: string;
  nav?: boolean;
  navGroup?: boolean;
  navFeature?: string;
  routes?: Record<string, RouteNode>;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const isParametric = (route: string) => route.includes(":");

const navLinkClass = ({ isActive }: { isActive: boolean }) =>
  C(
    "flex w-full p-2.5 p-menuitem-link",
    isActive ? `text-color ${themeClasses.bgElevation3} rounded` : "text-secondary"
  );

// ---------------------------------------------------------------------------
// NavGroup — collapsible section header (no link, toggles children)
// ---------------------------------------------------------------------------

function NavGroup({ entry, depth = 0 }: { entry: RouteNode; depth?: number }) {
  const location = useLocation();
  const childRoutes = entry.routes ? Object.values(entry.routes) : [];
  const children = childRoutes.filter(child => child.nav);

  const isChildActive = entry.route ? location.pathname.startsWith(entry.route) : false;
  const [expanded, setExpanded] = useState(isChildActive);

  const childContainerId = `nav-group-${entry.name?.toLowerCase().replace(/\s+/g, "-")}`;

  useEffect(() => {
    if (isChildActive) setExpanded(true);
  }, [isChildActive]);

  return (
    <div>
      <button
        type="button"
        aria-expanded={expanded}
        aria-controls={childContainerId}
        data-testid={childContainerId}
        onClick={() => setExpanded(prev => !prev)}
        className="flex w-full cursor-pointer items-center justify-between p-2.5 p-menuitem-link text-secondary transition-colors duration-500"
        style={depth > 0 ? { paddingLeft: `${0.75 + depth * 0.75}rem` } : undefined}
      >
        <span>{entry.name}</span>
        <i
          className={`pi pi-chevron-down body-xs transition-transform duration-500 ${expanded ? "rotate-180" : ""}`}
        />
      </button>
      <div
        id={childContainerId}
        className={`overflow-hidden ${expanded ? "max-h-96" : "max-h-0"} duration-700 ease-in-out`}
      >
        {children.length > 0 && (
          <div className="space-y-1">
            {children.map((child, idx) => (
              <NavItem key={child.name ? child.name + idx : idx} entry={child} depth={depth + 1} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// NavItem — recursive side-panel link
// ---------------------------------------------------------------------------

function NavItem({ entry, depth = 0 }: { entry: RouteNode; depth?: number }) {
  if (entry.navGroup) {
    return <NavGroup entry={entry} depth={depth} />;
  }

  const hasLink = entry.name && entry.route && !isParametric(entry.route);
  const childRoutes = entry.routes ? Object.values(entry.routes) : [];
  const children = childRoutes.filter(child => child.nav);

  return (
    <>
      {hasLink && (
        <NavLink
          to={entry.route!}
          end={entry.route === AdminAppRoutes.routes.dashboard.route}
          className={navLinkClass}
          data-testid={`nav-link-${sanitizeTestId(entry.name ?? "")}`}
          style={depth > 0 ? { paddingLeft: `${0.75 + depth * 0.75}rem` } : undefined}
        >
          {entry.name}
        </NavLink>
      )}

      {children.length > 0 && (
        <div className="space-y-1">
          {children.map((child, idx) => (
            <NavItem key={child.name ? child.name + idx : idx} entry={child} depth={depth + 1} />
          ))}
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Sidepanel
// ---------------------------------------------------------------------------

export default function Sidepanel() {
  const { features, isBackOfficeAspenEnabled, isLoading } = useAspenFeatures({ enabled: true });
  const { isFeatureEnabled } = useSettingsContext();
  const { isRestrictedBundle } = useUserContext();
  const hideQuests = isRestrictedBundle;

  const navEntries = useMemo(() => {
    const entries = (Object.values(AdminAppRoutes.routes) as RouteNode[]).filter(entry => {
      if (entry.nav !== true) return false;
      if (entry.navFeature && !isFeatureEnabled(entry.navFeature)) return false;
      if (
        entry.route === AdminAppRoutes.routes.aspen.route &&
        (isLoading || !isBackOfficeAspenEnabled)
      ) {
        return false;
      }
      return true;
    });

    return entries.map(entry => {
      if (entry.route === AdminAppRoutes.routes.paths.route) {
        if (!hideQuests || !entry.routes) {
          return entry;
        }

        const filteredRoutes: Record<string, RouteNode> = {};
        Object.entries(entry.routes).forEach(([key, child]) => {
          if (child.route === AdminAppRoutes.routes.paths.routes.quests.route) {
            filteredRoutes[key] = { ...child, nav: false };
          } else {
            filteredRoutes[key] = child;
          }
        });

        return { ...entry, name: "Paths", routes: filteredRoutes };
      }

      if (entry.route !== AdminAppRoutes.routes.aspen.route) {
        return entry;
      }

      const aspenRoutes = entry.routes ?? {};
      const filteredAspenRoutes: Record<string, RouteNode> = {};
      if (aspenRoutes.settings) {
        filteredAspenRoutes.settings = { ...aspenRoutes.settings, nav: true };
      }
      if (aspenRoutes.assist && features.assist) {
        filteredAspenRoutes.assist = { ...aspenRoutes.assist, nav: true };
      }
      if (aspenRoutes.guardian && features.guardian) {
        filteredAspenRoutes.guardian = { ...aspenRoutes.guardian, nav: true };
      }
      if (aspenRoutes.adapt && features.adapt) {
        filteredAspenRoutes.adapt = { ...aspenRoutes.adapt, nav: true };
      }

      return {
        ...entry,
        navGroup: true,
        routes: filteredAspenRoutes,
      };
    });
  }, [
    features.adapt,
    features.assist,
    features.guardian,
    isBackOfficeAspenEnabled,
    isFeatureEnabled,
    isLoading,
    hideQuests,
  ]);

  return (
    <aside
      data-testid="admin-sidebar"
      className="fixed left-1 w-60 flex flex-col print:hidden"
      style={{ top: "12px", height: "calc(100vh - 12px)" }}
    >
      {/* Spacer matching fixed header height — kept outside the scroll container so the scrollbar track starts below the header */}
      <div className="h-[60px] flex-shrink-0" aria-hidden="true" />
      <nav className={`flex-1 overflow-y-auto space-y-2 pb-4 ${themeClasses.scrollbar}`}>
        {navEntries.map((entry, idx) => (
          <NavItem key={entry.name ? entry.name + idx : idx} entry={entry} />
        ))}
      </nav>
    </aside>
  );
}
